/* ============================================================================
 * Toast —— 修复 V2 实测缺陷：
 *   F-50  V2 toast 无 aria-live / role=status，对读屏用户完全静默
 *   F-49  V2 toast 不可手动关闭
 *   F-62  V2 全站 aria-live 区域为 0，求解完成/审批迁移等状态变更全部静默
 * 本模块同时充当全站的"礼貌播报"通道：非 toast 的状态变更也可走 announce()。
 * ========================================================================== */
import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from './Icon';

export type ToastTone = 'ok' | 'warn' | 'danger' | 'info';

interface ToastItem {
  id: number;
  tone: ToastTone;
  text: ReactNode;
  /** ms；0 表示不自动消失（错误类默认不自动消失）*/
  duration: number;
}

const ICON: Record<ToastTone, IconName> = { ok: 'check', warn: 'warn', danger: 'warn', info: 'info' };

let seq = 0;
const listeners = new Set<(items: ToastItem[]) => void>();
let items: ToastItem[] = [];
/** 独立于 toast 的纯播报文本，供 aria-live 使用 */
let liveMessage = '';
const liveListeners = new Set<(m: string) => void>();

function emit() { listeners.forEach((fn) => fn(items)); }
function emitLive() { liveListeners.forEach((fn) => fn(liveMessage)); }

export function toast(text: ReactNode, tone: ToastTone = 'info', duration?: number) {
  const id = ++seq;
  const d = duration ?? (tone === 'danger' ? 0 : 4000);
  items = [...items, { id, tone, text, duration: d }];
  emit();
  if (typeof text === 'string') announce(text);
  if (d > 0) window.setTimeout(() => dismiss(id), d);
  return id;
}

export function dismiss(id: number) {
  items = items.filter((t) => t.id !== id);
  emit();
}

/** 只播报不显形——用于求解完成、审批状态迁移这类"视觉上已有反馈"的场景 */
export function announce(message: string) {
  // 同文案连播时读屏可能不重读，加零宽空格强制变化
  liveMessage = liveMessage === message ? message + '​' : message;
  emitLive();
}

export function ToastHost() {
  const [list, setList] = useState<ToastItem[]>(items);
  const [live, setLive] = useState(liveMessage);

  useEffect(() => {
    listeners.add(setList);
    liveListeners.add(setLive);
    return () => { listeners.delete(setList); liveListeners.delete(setLive); };
  }, []);

  const close = useCallback((id: number) => dismiss(id), []);

  return createPortal(
    <>
      {/* 全站唯一的礼貌播报区。V2 此处为 0 个，所有状态变更对读屏静默。 */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">{live}</div>

      <div className="toast-host">
        {list.map((t) => (
          <div key={t.id} className={`toast toast--${t.tone}`} role={t.tone === 'danger' ? 'alert' : undefined}>
            <Icon name={ICON[t.tone]} size={15} />
            <span className="toast-text">{t.text}</span>
            <button type="button" className="toast-close" onClick={() => close(t.id)} aria-label="关闭提示">
              <Icon name="close" size={13} />
            </button>
          </div>
        ))}
      </div>
    </>,
    document.body,
  );
}
